import {
  SearchEngine,
  SearchEngineConfiguration,
  buildContext,
  buildDictionaryFieldContext,
  buildSearchEngine,
  getOrganizationEndpoints,
} from '@coveo/atomic-react';
import { analyticsClientMiddleware } from '@lib/coveo/analyticsClientMiddleware';
import { CoveoSearchHub } from '@lib/coveo/coveoConstants';
import { interceptSearchMiddleware } from '@lib/coveo/interceptSearchMiddleware';
import {
  getNewSearchToken,
  getSavedSearchToken,
  getSearchTokenGroups,
} from '@lib/coveo/searchTokenHelpers';
import { clearStableFacetValues } from './hooks/useStableFacetValues';

export type InitEngineOptions = {
  locale: string;
  currency: string;
  analyticsEnabled: boolean;
};

export function createEngineModule(hub: CoveoSearchHub): {
  initEngine: (options: InitEngineOptions) => Promise<SearchEngine>;
  engine: () => SearchEngine;
  resetEngine: () => void;
} {
  let singleton: SearchEngine | null = null;
  let initPromise: Promise<SearchEngine> | null = null;

  function getConfiguration(
    searchToken: string,
    analyticsEnabled: boolean,
    locale: string
  ): SearchEngineConfiguration {
    const organizationId = process.env.NEXT_PUBLIC_COVEO_ORGANIZATION_ID!;
    return {
      accessToken: searchToken,
      organizationId,
      organizationEndpoints: getOrganizationEndpoints(organizationId),
      renewAccessToken: () => getNewSearchToken(hub),
      name: hub,
      search: { searchHub: hub, locale },
      analytics: { enabled: analyticsEnabled, analyticsClientMiddleware },
    };
  }

  function updateContext(eng: SearchEngine, options: InitEngineOptions): void {
    buildDictionaryFieldContext(eng).set({
      ec_price: options.currency,
      ec_price_filter: options.currency,
    });
    buildContext(eng).add('language', options.locale);
  }

  async function doInit(options: InitEngineOptions): Promise<SearchEngine> {
    const { locale, currency, analyticsEnabled } = options;

    const savedToken = getSavedSearchToken(hub);
    let searchToken: string | null = null;

    if (savedToken && savedToken !== '{}') {
      try {
        const parsed = JSON.parse(savedToken);
        if (typeof parsed === 'string' && parsed.trim()) {
          searchToken = parsed;
        }
      } catch {
        // fall through to fetch a new token
      }
    }

    if (!searchToken) {
      searchToken = await getNewSearchToken(hub);
    }

    if (!searchToken) {
      throw new Error(
        `Failed to initialize Coveo engine (${hub}): missing search token.`
      );
    }

    singleton = buildSearchEngine({
      configuration: getConfiguration(searchToken, analyticsEnabled, locale),
      middlewares: [interceptSearchMiddleware],
    });

    buildDictionaryFieldContext(singleton).set({
      ec_price: currency,
      ec_price_filter: currency,
    });
    const context = buildContext(singleton);
    context.add('website', 'assetstore');
    context.add('language', locale);
    context.add('userGroups', getSearchTokenGroups(searchToken) || []);

    return singleton;
  }

  function initEngine(options: InitEngineOptions): Promise<SearchEngine> {
    if (singleton) {
      updateContext(singleton, options);
      return Promise.resolve(singleton);
    }

    if (initPromise) return initPromise;

    initPromise = doInit(options).catch((error) => {
      initPromise = null;
      throw error;
    });

    return initPromise;
  }

  function engine(): SearchEngine {
    if (!singleton) {
      throw new Error(
        `Coveo engine (${hub}) not initialized. Call initEngine() before using engine().`
      );
    }
    return singleton;
  }

  function resetEngine(): void {
    if (singleton) clearStableFacetValues(singleton);
    singleton = null;
    initPromise = null;
  }

  return { initEngine, engine, resetEngine };
}
